import axios from 'axios';

/** Si REACT_APP_BACKEND_URL está vacío (producción con proxy nginx), usar /api en el mismo host. */
export function getApiBase() {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (raw == null || String(raw).trim() === '') {
    return '/api';
  }
  return `${String(raw).replace(/\/$/, '')}/api`;
}

/** Para rutas de imágenes absolutas; vacío => rutas relativas (/api/product-images/...). */
export function getPublicApiOrigin() {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (raw == null || String(raw).trim() === '') {
    return '';
  }
  return String(raw).replace(/\/$/, '');
}

/** En página HTTPS, evita contenido mixto (imágenes/enlaces guardados como http://). */
function preferHttpsAbsolute(url) {
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    url.startsWith('http://')
  ) {
    return `https://${url.slice(7)}`;
  }
  return url;
}

/** URL utilizable en <img src> (absolutas, /api/... o rutas relativas con proxy). */
export function resolveMediaUrl(url) {
  if (url == null || String(url).trim() === '') return '';
  const u = String(url).trim();
  if (u.startsWith('http://') || u.startsWith('https://')) {
    return preferHttpsAbsolute(u);
  }
  const path = u.startsWith('/') ? u : `/${u}`;
  const origin = getPublicApiOrigin();
  const built = origin ? `${origin}${path}` : path;
  return preferHttpsAbsolute(built);
}

/**
 * URL absoluta para documentos en blob: o ventana nueva (p. ej. impresión PDF).
 * Las rutas /api/... deben incluir el origen del sitio, no solo el path relativo.
 */
export function resolveAbsoluteMediaUrl(url) {
  const resolved = resolveMediaUrl(url);
  if (!resolved) return '';
  if (resolved.startsWith('http://') || resolved.startsWith('https://')) {
    return resolved;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    const path = resolved.startsWith('/') ? resolved : `/${resolved}`;
    return `${window.location.origin}${path}`;
  }
  return resolved;
}

const API = getApiBase();

/** Cliente HTTP compartido (cookies + Bearer + renovación silenciosa de sesión). */
export const apiClient = axios.create({
  withCredentials: true,
});

let refreshInFlight = null;
let sessionExpiredHandled = false;
let onTokenRefreshed = null;
let onSessionExpired = null;

export function registerAuthSessionHandlers(handlers = {}) {
  if (handlers.onTokenRefreshed) onTokenRefreshed = handlers.onTokenRefreshed;
  if (handlers.onSessionExpired) onSessionExpired = handlers.onSessionExpired;
}

export function applyAccessTokenToStorage(token) {
  const t = String(token || '').trim();
  if (t) {
    localStorage.setItem('token', t);
  } else {
    localStorage.removeItem('token');
  }
  return t;
}

export function resetSessionExpiredFlag() {
  sessionExpiredHandled = false;
}

function isAuthExemptUrl(url) {
  const path = String(url || '');
  return (
    path.includes('/auth/login')
    || path.includes('/auth/refresh')
    || path.includes('/auth/logout')
  );
}

function redirectToLoginSessionExpired() {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  applyAccessTokenToStorage(null);
  if (typeof onSessionExpired === 'function') {
    onSessionExpired();
  }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin/login')) {
    window.location.href = '/admin/login?reason=session_expired';
  }
}

async function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = apiClient
      .post(`${API}/auth/refresh`, {})
      .then((res) => {
        const newToken = applyAccessTokenToStorage(res.data?.token);
        if (!newToken) {
          throw new Error('refresh sin token');
        }
        if (typeof onTokenRefreshed === 'function') {
          onTokenRefreshed(newToken);
        }
        return newToken;
      })
      .catch((err) => {
        redirectToLoginSessionExpired();
        throw err;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const original = error.config;
    if (
      status !== 401
      || !original
      || original._retryAfterRefresh
      || isAuthExemptUrl(original.url)
    ) {
      return Promise.reject(error);
    }
    try {
      const newToken = await refreshAccessToken();
      original._retryAfterRefresh = true;
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newToken}`;
      return apiClient.request(original);
    } catch (refreshErr) {
      return Promise.reject(refreshErr);
    }
  },
);

// Products
export const getProducts = async (activeOnly = true) => {
  const response = await apiClient.get(`${API}/products?active_only=${activeOnly}`);
  return response.data;
};

export const getProduct = async (id) => {
  const response = await apiClient.get(`${API}/products/${id}`);
  return response.data;
};

export const createProduct = async (product) => {
  const response = await apiClient.post(`${API}/admin/products`, product);
  return response.data;
};

export const updateProduct = async (id, product) => {
  const response = await apiClient.put(`${API}/admin/products/${id}`, product);
  return response.data;
};

export const deleteProduct = async (id) => {
  const response = await apiClient.delete(`${API}/admin/products/${id}`);
  return response.data;
};

export const uploadProductImage = async (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`${API}/admin/products/${id}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Appointments
export const getAvailableSlots = async (date, options = {}) => {
  const params = new URLSearchParams({ date });
  if (options.kind) {
    params.set('kind', options.kind);
  }
  const response = await apiClient.get(`${API}/available-slots?${params.toString()}`);
  return response.data;
};

export const createAppointment = async (appointment) => {
  const response = await apiClient.post(`${API}/appointments`, appointment);
  return response.data;
};

export const createAdminAppointment = async (appointment) => {
  const response = await apiClient.post(`${API}/admin/appointments`, appointment);
  return response.data;
};

export const getAppointments = async (status = null) => {
  const url = status ? `${API}/appointments?status=${status}` : `${API}/appointments`;
  const response = await apiClient.get(url);
  return response.data;
};

export const updateAppointment = async (id, data) => {
  const response = await apiClient.put(`${API}/admin/appointments/${id}`, data);
  return response.data;
};

export const deleteAdminAppointment = async (appointmentId) => {
  const response = await apiClient.delete(`${API}/admin/appointments/${appointmentId}`);
  return response.data;
};

export const markAppointmentNoShow = async (appointmentId) => {
  const response = await apiClient.post(
    `${API}/admin/appointments/${appointmentId}/mark-no-show`,
    {},
  );
  return response.data;
};

export const restoreAppointmentAfterAttendanceCancel = async (appointmentId) => {
  const response = await apiClient.post(
    `${API}/admin/appointments/${appointmentId}/restore-after-attendance-cancel`,
    {},
  );
  return response.data;
};

export const getBookingPhoneStats = async ({ minCount = 1, atLimitOnly = false } = {}) => {
  const params = new URLSearchParams({
    min_count: String(minCount),
    at_limit_only: atLimitOnly ? 'true' : 'false',
  });
  const response = await apiClient.get(`${API}/admin/booking-phone-stats?${params}`);
  return response.data;
};

export const resetPhoneNoShowCount = async (phone) => {
  const response = await apiClient.post(
    `${API}/admin/booking-phone-stats/reset-no-show`,
    { phone },
  );
  return response.data;
};

export const getAdminCalendarEvents = async (startAt, endAt) => {
  const response = await apiClient.get(
    `${API}/admin/calendar-events?start_at=${encodeURIComponent(startAt)}&end_at=${encodeURIComponent(endAt)}`,
  );
  return response.data;
};

// Reviews
export const getReviews = async (approvedOnly = true) => {
  const response = await apiClient.get(`${API}/reviews?approved_only=${approvedOnly}`);
  return response.data;
};

export const getAllReviews = async (status = null) => {
  const url = status ? `${API}/admin/reviews?status=${status}` : `${API}/admin/reviews`;
  const response = await apiClient.get(url);
  return response.data;
};

export const createReview = async (review) => {
  const response = await apiClient.post(`${API}/reviews`, review);
  return response.data;
};

export const updateReview = async (id, data) => {
  const response = await apiClient.put(`${API}/admin/reviews/${id}`, data);
  return response.data;
};

export const deleteReview = async (id) => {
  const response = await apiClient.delete(`${API}/admin/reviews/${id}`);
  return response.data;
};

// Schedule
export const getSchedule = async () => {
  const response = await apiClient.get(`${API}/schedule`);
  return response.data;
};

export const updateSchedule = async (schedule) => {
  const response = await apiClient.put(`${API}/admin/schedule`, schedule);
  return response.data;
};

export const getCalendarExceptions = async () => {
  const response = await apiClient.get(`${API}/calendar-exceptions`);
  return response.data;
};

export const createCalendarException = async (exception) => {
  const response = await apiClient.post(`${API}/admin/calendar-exceptions`, exception);
  return response.data;
};

export const deleteCalendarException = async (id) => {
  const response = await apiClient.delete(`${API}/admin/calendar-exceptions/${id}`);
  return response.data;
};

// Clinic Info
export const getClinicInfo = async () => {
  const response = await apiClient.get(`${API}/clinic-info`);
  return response.data;
};

export const updateClinicInfo = async (info) => {
  const response = await apiClient.put(`${API}/admin/clinic-info`, info);
  return response.data;
};

/** Sube imagen JPG/PNG/GIF/WebP para sitio público. Respuesta { image_url }. Requiere super_admin. */
export const uploadClinicSiteImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`${API}/admin/clinic-site-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/** Solo super_admin */
export const getAdminUsers = async () => {
  const response = await apiClient.get(`${API}/admin/admin-users`);
  return response.data;
};

export const createAdminUser = async (payload) => {
  const response = await apiClient.post(`${API}/admin/admin-users`, payload);
  return response.data;
};

export const deleteAdminUser = async (userId) => {
  const response = await apiClient.delete(`${API}/admin/admin-users/${userId}`);
  return response.data;
};

export const getAdminSettings = async () => {
  const response = await apiClient.get(`${API}/admin/settings`);
  return response.data;
};

export const updateAdminSettings = async (settings) => {
  const response = await apiClient.put(`${API}/admin/settings`, settings);
  return response.data;
};

export const sendDailyRemindersNow = async (force = false) => {
  const response = await apiClient.post(
    `${API}/admin/reminders/daily-send`,
    { force },
  );
  return response.data;
};

export const uploadWhatsappTreeGreetingImage = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await apiClient.post(`${API}/admin/whatsapp-tree-greeting-image`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadWhatsappTreeFollowupImage = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await apiClient.post(`${API}/admin/whatsapp-tree-followup-image`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Stats
export const getAdminStats = async () => {
  const response = await apiClient.get(`${API}/admin/stats`);
  return response.data;
};

/** Estado del puente Baileys (vinculado, QR data URL, etc.). Requiere admin. */
export const getWhatsAppLinkStatus = async () => {
  const response = await apiClient.get(`${API}/admin/whatsapp-link`);
  return response.data;
};

/** Reinicia sesión del puente (nuevo QR). Requiere admin. */
export const resetWhatsAppBridgeSession = async () => {
  const response = await apiClient.post(`${API}/admin/whatsapp-link/reset`, {});
  return response.data;
};

/** Envío manual a un número (vía puente Baileys). Requiere admin y sesión WhatsApp vinculada. */
export const sendWhatsAppAdmin = async (phone, message) => {
  const response = await apiClient.post(
    `${API}/admin/whatsapp-send`,
    { phone, message },
  );
  return response.data;
};

/** Estado de envío WhatsApp por requestId: queued/sent/delivered/read/failed. */
export const getWhatsAppSendStatus = async (requestId) => {
  const response = await apiClient.get(`${API}/admin/whatsapp-send/${encodeURIComponent(requestId)}`);
  return response.data;
};

// --- Expedientes clínicos ---
export const listPatientRecords = async (q = '') => {
  const params = q ? { q } : {};
  const response = await apiClient.get(`${API}/admin/patient-records`, {
    params,
  });
  return response.data;
};

export const createPatientWithRecord = async (payload) => {
  const response = await apiClient.post(`${API}/admin/patients`, payload);
  return response.data;
};

export const getPatientRecord = async (recordId) => {
  const response = await apiClient.get(`${API}/admin/patient-records/${recordId}`);
  return response.data;
};

export const patchPatientRecord = async (recordId, payload) => {
  const response = await apiClient.patch(`${API}/admin/patient-records/${recordId}`, payload);
  return response.data;
};

export const deletePatientRecord = async (recordId) => {
  const response = await apiClient.delete(`${API}/admin/patient-records/${recordId}`);
  return response.data;
};

export const patchPatient = async (patientId, payload) => {
  const response = await apiClient.patch(`${API}/admin/patients/${patientId}`, payload);
  return response.data;
};

export const searchPatients = async ({ q = '', phone = '' } = {}) => {
  const params = {};
  if (phone && String(phone).replace(/\D/g, '').length >= 10) {
    params.phone = phone;
  } else if (q && String(q).trim()) {
    params.q = String(q).trim();
  }
  const response = await apiClient.get(`${API}/admin/patients/search`, {
    params,
  });
  return response.data;
};

export const createClinicalEncounter = async (recordId, payload) => {
  const response = await apiClient.post(
    `${API}/admin/patient-records/${recordId}/encounters`,
    payload,
  );
  return response.data;
};

export const getClinicalEncounter = async (encounterId) => {
  const response = await apiClient.get(`${API}/admin/encounters/${encounterId}`);
  return response.data;
};

export const patchClinicalEncounter = async (encounterId, payload) => {
  const response = await apiClient.patch(`${API}/admin/encounters/${encounterId}`, payload);
  return response.data;
};

/** Payload de impresión de nota de evolución (textos desde MongoDB). */
export const getEncounterEvolutionNotePrint = async (encounterId) => {
  const response = await apiClient.get(
    `${API}/admin/encounters/${encounterId}/evolution-note-print`,
  );
  return response.data;
};

export const uploadClinicalAttachment = async (encounterId, file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await apiClient.post(
    `${API}/admin/encounters/${encounterId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

/** Alias usado en notas de evolución (encuentros). Folio NE-{año}-{seq} al completar. */
export const uploadEncounterAttachment = uploadClinicalAttachment;

/** Foto de alteración (dérmica / ungueal / digital) en historia clínica */
export const uploadIntakeAlterationPhoto = async (recordId, zone, file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await apiClient.post(
    `${API}/admin/patient-records/${recordId}/intake-attachments?zone=${encodeURIComponent(zone)}`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const getAppointmentClinicalContext = async (appointmentId) => {
  const response = await apiClient.get(
    `${API}/admin/appointments/${appointmentId}/clinical-context`,
  );
  return response.data;
};

export const linkAppointmentPatientRecord = async (appointmentId, recordId) => {
  const response = await apiClient.patch(
    `${API}/admin/appointments/${appointmentId}/link-patient-record`,
    { patient_record_id: recordId },
  );
  return response.data;
};

export const unlinkAppointmentPatientRecord = async (appointmentId) => {
  const response = await apiClient.delete(
    `${API}/admin/appointments/${appointmentId}/link-patient-record`,
  );
  return response.data;
};

export const createPatientRecordFromAppointment = async (appointmentId, payload = {}) => {
  const response = await apiClient.post(
    `${API}/admin/appointments/${appointmentId}/create-patient-record`,
    payload,
  );
  return response.data;
};

export const matchPatientsForAppointment = async (appointmentId) => {
  const response = await apiClient.get(
    `${API}/admin/patients/match-for-appointment/${appointmentId}`,
  );
  return response.data;
};

export const SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE = 'BORRAR TODAS LAS CITAS';
export const SUPER_RESET_CLINICAL_CONFIRM_PHRASE = 'BORRAR TODAS LAS HISTORIAS CLINICAS';

export const superAdminResetAllAppointments = async (confirmPhrase) => {
  const response = await apiClient.post(
    `${API}/admin/super/reset-appointments`,
    { confirm_phrase: confirmPhrase },
  );
  return response.data;
};

export const superAdminResetAllClinicalRecords = async (confirmPhrase) => {
  const response = await apiClient.post(
    `${API}/admin/super/reset-clinical-records`,
    { confirm_phrase: confirmPhrase },
  );
  return response.data;
};
